// ============================================================
// ESPN API Service
// Fonte: API não oficial da ESPN (sem autenticação necessária)
// ============================================================

const ESPN_BASE = 'https://site.api.espn.com/apis/site/v2/sports/football/nfl'
const ESPN_CORE = 'https://sports.core.api.espn.com/v2/sports/football/leagues/nfl'

// Tipos de temporada ESPN:
// 1 = pré-temporada, 2 = temporada regular, 3 = playoffs

// ============================================================
// JOGOS DA SEMANA
// Retorna todos os jogos de uma semana específica
// ============================================================
export async function buscarJogosDaSemana(ano, semana, tipoTemporada = 2) {
  try {
    // Tenta primeiro o endpoint de events por temporada/semana (mais confiável para temporadas futuras)
    const urlEvents = `${ESPN_CORE}/seasons/${ano}/types/${tipoTemporada}/weeks/${semana}/events?limit=25`
    const resEvents = await fetch(urlEvents)

    if (resEvents.ok) {
      const dataEvents = await resEvents.json()
      // Endpoint retorna lista de $ref, precisa buscar cada evento
      const refs = (dataEvents.items || []).map(i => i.$ref).filter(Boolean)

      if (refs.length > 0) {
        const jogos = await Promise.all(refs.map(async ref => {
          try {
            const r = await fetch(ref.replace('http://', 'https://'))
            if (!r.ok) return null
            const evento = await r.json()
            if (!evento.id) return null

            const comp = evento.competitions?.[0]
            const timeCasa = comp?.competitors?.find(t => t.homeAway === 'home')
            const timeVisitante = comp?.competitors?.find(t => t.homeAway === 'away')

            // Usa abbreviation dos competitors como fonte primária (mais confiável que shortName)
            // ESPN às vezes usa "LA" em shortName mas "LAR" em team.abbreviation
            const abbrCasa = timeCasa?.team?.abbreviation || ''
            const abbrVisitante = timeVisitante?.team?.abbreviation || ''
            const nomeCasa = timeCasa?.team?.displayName || ''
            const nomeVisitante = timeVisitante?.team?.displayName || ''

            if (!abbrCasa || !abbrVisitante) return null

            return {
              espn_event_id: String(evento.id),
              time_casa: abbrCasa,
              time_visitante: abbrVisitante,
              nome_casa: nomeCasa,
              nome_visitante: nomeVisitante,
              horario: evento.date,
              status: comp?.status?.type?.name || 'STATUS_SCHEDULED',
              status_detalhe: comp?.status?.type?.shortDetail || null,
              periodo: comp?.status?.period || null,
              tempo_restante: comp?.status?.displayClock || null,
              placar_casa: timeCasa?.score || null,
              placar_visitante: timeVisitante?.score || null,
              resultado: resolverResultado(comp || {}),
            }
          } catch { return null }
        }))
        const jogosValidos = jogos.filter(j => j && j.time_casa && j.time_visitante)
        if (jogosValidos.length > 0) return { sucesso: true, jogos: jogosValidos }
      }
    }

    // Fallback: endpoint scoreboard padrão
    const url = `${ESPN_BASE}/scoreboard?dates=${ano}&seasontype=${tipoTemporada}&week=${semana}`
    const res = await fetch(url)
    if (!res.ok) throw new Error(`ESPN scoreboard error: ${res.status}`)
    const data = await res.json()

    const jogos = (data.events || []).map(evento => {
      const comp = evento.competitions[0]
      const timeCasa = comp.competitors.find(t => t.homeAway === 'home')
      const timeVisitante = comp.competitors.find(t => t.homeAway === 'away')

      return {
        espn_event_id: evento.id,
        time_casa: timeCasa?.team?.abbreviation || '',
        time_visitante: timeVisitante?.team?.abbreviation || '',
        nome_casa: timeCasa?.team?.displayName || '',
        nome_visitante: timeVisitante?.team?.displayName || '',
        horario: evento.date,
        status: comp.status?.type?.name || 'STATUS_SCHEDULED', // STATUS_SCHEDULED | STATUS_IN_PROGRESS | STATUS_FINAL
        status_detalhe: comp.status?.type?.shortDetail || null,
        periodo: comp.status?.period || null,
        tempo_restante: comp.status?.displayClock || null,
        placar_casa: timeCasa?.score || null,
        placar_visitante: timeVisitante?.score || null,
        resultado: resolverResultado(comp),
      }
    })

    return { sucesso: true, jogos }
  } catch (err) {
    console.error('Erro ao buscar jogos ESPN:', err)
    return { sucesso: false, jogos: [], erro: err.message }
  }
}

// ============================================================
// SPREADS DE UM JOGO
// Retorna os spreads disponíveis para um evento específico
// ESPN usa provider ID 1003 para o consensus/ESPN próprio
// ============================================================
export async function buscarSpreadsDoJogo(eventoId) {
  try {
    const url = `${ESPN_CORE}/events/${eventoId}/competitions/${eventoId}/odds`
    const res = await fetch(url)
    if (!res.ok) throw new Error(`ESPN odds error: ${res.status}`)
    const data = await res.json()

    const items = data.items || []

    // Pega o primeiro provedor disponível (ESPN consensus ou outro)
    const provedor = items[0]
    if (!provedor) return { sucesso: true, spread_casa: null, spread_visitante: null }

    const spreadCasa = provedor.spread ?? null
    // Spread da casa é negativo se favorito; visitante é o inverso
    const spreadVisitante = spreadCasa !== null ? spreadCasa * -1 : null

    return {
      sucesso: true,
      spread_casa: spreadCasa,
      spread_visitante: spreadVisitante,
      provedor: provedor.provider?.name || 'ESPN',
      atualizado_em: new Date().toISOString(),
    }
  } catch (err) {
    console.error(`Erro ao buscar spread evento ${eventoId}:`, err)
    return { sucesso: false, spread_casa: null, spread_visitante: null, erro: err.message }
  }
}

// ============================================================
// SPREADS DE TODOS OS JOGOS DA SEMANA
// Combina buscarJogosDaSemana + buscarSpreadsDoJogo
// ============================================================
export async function buscarJogosComSpreads(ano, semana, tipoTemporada = 2) {
  const { sucesso, jogos, erro } = await buscarJogosDaSemana(ano, semana, tipoTemporada)
  if (!sucesso) return { sucesso: false, jogos: [], erro }

  // Busca spreads em paralelo para todos os jogos
  const jogosComSpreads = await Promise.all(
    jogos.map(async jogo => {
      const spreads = await buscarSpreadsDoJogo(jogo.espn_event_id)
      return {
        ...jogo,
        spread_casa: spreads.spread_casa,
        spread_visitante: spreads.spread_visitante,
        spread_provedor: spreads.provedor,
      }
    })
  )

  return { sucesso: true, jogos: jogosComSpreads }
}

// ============================================================
// NOTÍCIAS POR TIME
// Retorna as últimas notícias de um time específico
// ============================================================
const TEAM_NAMES = {
  ARI: ['Arizona', 'Cardinals'],
  ATL: ['Atlanta', 'Falcons'],
  BAL: ['Baltimore', 'Ravens'],
  BUF: ['Buffalo', 'Bills'],
  CAR: ['Carolina', 'Panthers'],
  CHI: ['Chicago', 'Bears'],
  CIN: ['Cincinnati', 'Bengals'],
  CLE: ['Cleveland', 'Browns'],
  DAL: ['Dallas', 'Cowboys'],
  DEN: ['Denver', 'Broncos'],
  DET: ['Detroit', 'Lions'],
  GB: ['Green Bay', 'Packers'],
  HOU: ['Houston', 'Texans'],
  IND: ['Indianapolis', 'Colts'],
  JAX: ['Jacksonville', 'Jaguars'],
  KC: ['Kansas City', 'Chiefs'],
  LAC: ['Los Angeles Chargers', 'Chargers'],
  LAR: ['Los Angeles Rams', 'Rams'],
  LV: ['Las Vegas', 'Raiders'],
  MIA: ['Miami', 'Dolphins'],
  MIN: ['Minnesota', 'Vikings'],
  NE: ['New England', 'Patriots'],
  NO: ['New Orleans', 'Saints'],
  NYG: ['New York Giants', 'Giants'],
  NYJ: ['New York Jets', 'Jets'],
  PHI: ['Philadelphia', 'Eagles'],
  PIT: ['Pittsburgh', 'Steelers'],
  SEA: ['Seattle', 'Seahawks'],
  SF: ['San Francisco', '49ers'],
  TB: ['Tampa Bay', 'Buccaneers'],
  TEN: ['Tennessee', 'Titans'],
  WSH: ['Washington', 'Commanders']
}

const TEAM_IDS = {
  ARI: 22, ATL: 1, BAL: 33, BUF: 2, CAR: 29, CHI: 3, CIN: 4, CLE: 5,
  DAL: 6, DEN: 7, DET: 8, GB: 9, HOU: 34, IND: 11, JAX: 30, KC: 12,
  LAC: 24, LAR: 14, LV: 13, MIA: 15, MIN: 16, NE: 17, NO: 18, NYG: 19,
  NYJ: 20, PHI: 21, PIT: 23, SEA: 26, SF: 25, TB: 27, TEN: 10, WSH: 28
}

export async function buscarNoticiasPorTime(teamAbbr) {
  try {
    const abbr = teamAbbr.toUpperCase()
    const teamId = TEAM_IDS[abbr]
    if (!teamId) throw new Error(`Time não encontrado: ${teamAbbr}`)

    // Busca mais artigos para ter margem após filtrar
    const url = `${ESPN_BASE}/news?team=${teamId}&limit=25`
    const res = await fetch(url)
    if (!res.ok) throw new Error(`ESPN news error: ${res.status}`)
    const data = await res.json()

    const noticias = (data.articles || [])
      .filter(a => {
        // Filtra apenas artigos que mencionam o time explicitamente no título ou descrição
        const names = TEAM_NAMES[abbr] || []
        const texto = `${a.headline || ''} ${a.description || ''}`.toLowerCase()
        return names.some(name => texto.includes(name.toLowerCase()))
      })
      .slice(0, 10)
      .map(a => ({
        titulo: a.headline,
        descricao: a.description,
        link: a.links?.web?.href || '',
        imagem: a.images?.[0]?.url || null,
        publicado_em: a.published,
        time: abbr,
      }))

    return { sucesso: true, noticias }
  } catch (err) {
    console.error(`Erro ao buscar notícias ${teamAbbr}:`, err)
    return { sucesso: false, noticias: [], erro: err.message }
  }
}

// ============================================================
// NOTÍCIAS GERAIS DA NFL
// ============================================================
export async function buscarNoticiasNFL(limite = 20) {
  try {
    const url = `${ESPN_BASE}/news?limit=${limite}`
    const res = await fetch(url)
    if (!res.ok) throw new Error(`ESPN news error: ${res.status}`)
    const data = await res.json()

    const noticias = (data.articles || []).map(a => ({
      titulo: a.headline,
      descricao: a.description,
      link: a.links?.web?.href || '',
      imagem: a.images?.[0]?.url || null,
      publicado_em: a.published,
      categorias: a.categories?.map(c => c.description).filter(Boolean) || [],
    }))

    return { sucesso: true, noticias }
  } catch (err) {
    console.error('Erro ao buscar notícias NFL:', err)
    return { sucesso: false, noticias: [], erro: err.message }
  }
}

// ============================================================
// TODOS OS TIMES DA NFL (para cache local)
// ============================================================
export async function buscarTodosTimes() {
  try {
    const url = `${ESPN_BASE}/teams?limit=32`
    const res = await fetch(url)
    if (!res.ok) throw new Error(`ESPN teams error: ${res.status}`)
    const data = await res.json()

    const times = (data.sports?.[0]?.leagues?.[0]?.teams || []).map(t => ({
      id: t.team.id,
      abbr: t.team.abbreviation,
      nome: t.team.displayName,
      cidade: t.team.location,
      apelido: t.team.name,
      logo: t.team.logos?.[0]?.href || null,
      cor: t.team.color ? `#${t.team.color}` : '#D85A30',
    }))

    return { sucesso: true, times }
  } catch (err) {
    console.error('Erro ao buscar times:', err)
    return { sucesso: false, times: [], erro: err.message }
  }
}

// ============================================================
// RECORDS DE TODOS OS TIMES DA TEMPORADA
// Retorna mapa { abbr: "W-L" } via scoreboard da semana informada
// ============================================================
export async function buscarRecordsTimes(semana, ano = 2026, tipoTemporada = 2) {
  try {
    const url = `${ESPN_BASE}/scoreboard?dates=${ano}&seasontype=${tipoTemporada}&week=${semana}`
    const res = await fetch(url)
    if (!res.ok) throw new Error()
    const data = await res.json()
    const records = {}
    for (const ev of data.events || []) {
      const comp = ev.competitions?.[0]
      for (const c of comp?.competitors || []) {
        const abbr = c.team?.abbreviation
        const r = (c.records || []).find(r => r.name === 'overall' || r.type === 'total')
        if (abbr && r?.summary) records[abbr] = r.summary
      }
    }
    return { sucesso: true, records }
  } catch {
    return { sucesso: false, records: {} }
  }
}

// ============================================================
// HELPER: determina resultado do jogo
// ============================================================
function resolverResultado(comp) {
  if (comp.status?.type?.name !== 'STATUS_FINAL') return null

  const casa = comp.competitors.find(t => t.homeAway === 'home')
  const visitante = comp.competitors.find(t => t.homeAway === 'away')
  const placarCasa = parseInt(casa?.score || 0)
  const placarVisitante = parseInt(visitante?.score || 0)

  if (placarCasa > placarVisitante) return 'casa'
  if (placarVisitante > placarCasa) return 'visitante'
  return 'empate'
}
